/**
 * Persists session_id ↔ meeting_id links in sessionStorage so a normal browser tab
 * can load Teams AI Assist history after the Teams iframe session ends.
 */

const STORAGE_KEY = "trek_session_meeting_map";

export type SessionMeetingMapEntry = {
  meeting_id: string;
  meeting_ids?: string[];
  type?: string;
  source?: string;
  chatType?: string;
};

type SessionMeetingMap = Record<string, SessionMeetingMapEntry>;

function readMap(): SessionMeetingMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as SessionMeetingMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map: SessionMeetingMap): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // quota / private mode
  }
}

export function persistSessionMeetingEntry(
  sessionId: string,
  entry: Partial<SessionMeetingMapEntry>
): void {
  const sid = sessionId?.trim();
  if (!sid) return;

  const mid =
    entry.meeting_id != null && String(entry.meeting_id).trim() !== ""
      ? String(entry.meeting_id).trim()
      : "";
  const mids =
    Array.isArray(entry.meeting_ids) && entry.meeting_ids.length > 0
      ? entry.meeting_ids
          .map((x) => String(x).trim())
          .filter((x) => x.length > 0)
      : mid
        ? [mid]
        : [];

  if (mids.length === 0 && !entry.type && !entry.source && !entry.chatType) {
    return;
  }

  const map = readMap();
  const prev = map[sid] ?? {};
  map[sid] = {
    ...prev,
    ...(mids[0] && { meeting_id: mids[0] }),
    ...(mids.length > 0 && { meeting_ids: mids }),
    ...(entry.type && { type: entry.type }),
    ...(entry.source && { source: entry.source }),
    ...(entry.chatType && { chatType: entry.chatType }),
  };
  writeMap(map);
}

export function getSessionMeetingEntry(
  sessionId: string
): SessionMeetingMapEntry | undefined {
  const sid = sessionId?.trim();
  if (!sid) return undefined;
  const map = readMap();
  if (map[sid]) return map[sid];
  const lower = sid.toLowerCase();
  for (const [key, entry] of Object.entries(map)) {
    if (key.toLowerCase() === lower) return entry;
  }
  return undefined;
}

export function getMeetingIdsForStoredSession(sessionId: string): string[] {
  const entry = getSessionMeetingEntry(sessionId);
  if (!entry) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (id: string) => {
    const t = id.trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };
  if (Array.isArray(entry.meeting_ids)) {
    for (const m of entry.meeting_ids) push(String(m));
  }
  if (entry.meeting_id) push(String(entry.meeting_id));
  return out;
}
